import React from "react";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import {
  GuidedEntryItem,
  findNextIncompleteIndex,
  getGenerationName,
} from "../utils/guidedEntryUtils";

interface GuidedPersonNavigatorProps {
  items: GuidedEntryItem[];
  selectedId: string | null;
  onSelect: (memberId: string) => void;
}

const statusIcon = (status: GuidedEntryItem["status"]) => {
  if (status === "complete") {
    return <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />;
  }
  return (
    <CircleIcon
      sx={{ fontSize: 12 }}
      color={status === "partial" ? "warning" : "disabled"}
    />
  );
};

/**
 * Sequential navigation for family tree entry: jump to any person from a
 * generation-grouped dropdown, step with prev/next, or skip straight to the
 * next person who still needs information.
 */
const GuidedPersonNavigator: React.FC<GuidedPersonNavigatorProps> = ({
  items,
  selectedId,
  onSelect,
}) => {
  if (items.length === 0) return null;

  const currentIndex = items.findIndex(
    (item) => item.member.id === selectedId
  );
  const completeCount = items.filter(
    (item) => item.status === "complete"
  ).length;
  const nextIncompleteIndex = findNextIncompleteIndex(
    items,
    currentIndex === -1 ? items.length - 1 : currentIndex
  );

  const selectByIndex = (index: number) => {
    const item = items[index];
    if (item) onSelect(item.member.id);
  };

  // Build dropdown children with generation group headers
  const dropdownChildren: React.ReactNode[] = [];
  let lastGeneration = -1;
  items.forEach((item) => {
    if (item.generation !== lastGeneration) {
      lastGeneration = item.generation;
      dropdownChildren.push(
        <ListSubheader key={`gen-${item.generation}`}>
          {getGenerationName(item.generation)}
        </ListSubheader>
      );
    }
    dropdownChildren.push(
      <MenuItem key={item.member.id} value={item.member.id}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {statusIcon(item.status)}
          <Typography variant="body2" component="span">
            {item.relationshipLabel}
          </Typography>
          {item.member.name && item.member.name !== item.relationshipLabel && (
            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 160,
              }}
            >
              — {item.member.name}
            </Typography>
          )}
        </Box>
      </MenuItem>
    );
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mb: 1,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 220, flexGrow: 1, maxWidth: 340 }}>
        <InputLabel id="guided-person-select-label">Family member</InputLabel>
        <Select
          labelId="guided-person-select-label"
          label="Family member"
          value={currentIndex === -1 ? "" : selectedId ?? ""}
          onChange={(e) => onSelect(e.target.value as string)}
          MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
        >
          {dropdownChildren}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Previous person">
          <span>
            <IconButton
              size="small"
              aria-label="previous person"
              disabled={currentIndex <= 0}
              onClick={() => selectByIndex(currentIndex - 1)}
            >
              <NavigateBeforeIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next person">
          <span>
            <IconButton
              size="small"
              aria-label="next person"
              disabled={currentIndex === -1 || currentIndex >= items.length - 1}
              onClick={() => selectByIndex(currentIndex + 1)}
            >
              <NavigateNextIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {nextIncompleteIndex !== -1 && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlaylistAddCheckIcon />}
          onClick={() => selectByIndex(nextIncompleteIndex)}
          sx={{ textTransform: "none" }}
        >
          Next to fill in
        </Button>
      )}

      <Chip
        size="small"
        label={`${completeCount} of ${items.length} complete`}
        color={completeCount === items.length ? "success" : "default"}
        variant="outlined"
      />
    </Box>
  );
};

export default GuidedPersonNavigator;
